import classes from "./Row_display.module.css";
import {Button, Tooltip} from "@mui/material";
import {useEffect, useState} from "react";
import axios from "axios";
import {path} from "../../../routes";

const Row_display = (props) => {
    const data = props.data;
    const [holidayCards, setHolidayCards] = useState([]);

    const handleEdit = () => {
        props.onEdit(data);
    };

    const handleDelete = () => {
        if (props.onDeleteRequest) {
            props.onDeleteRequest(data);
        } else if (props.onDelete) {
            props.onDelete(data.id);
        }
    };

    const copyToClipboard = async (text) => {
        try {
            const value = String(text);
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(value);
                alert('Link copied to clipboard.');
                return true;
            }
            const ta = document.createElement('textarea');
            ta.value = value;
            ta.setAttribute('readonly', '');
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            const ok = document.execCommand('copy');
            document.body.removeChild(ta);
            if (ok) {
                alert('Link copied to clipboard.');
            }
            return ok;
        } catch (e) {
            return false;
        }
    };

    const HOLIDAY_INN_ORG_ID = 'E15F8010-B7EB-4526-A985-F00D517EBF4C';
    const RADISSON_BLU_ORG_ID = 'AC9D7FD5-E333-4B1E-9043-4697568313B7';
    // "Legacy" hotely, kde URL musí zůstat v původním formátu s orgID
    // (per dokumentace XLSX). Pro všechny ostatní (nové) orgy se generuje
    // krátká URL bez orgID — backend si org dohledá z device.id.
    const LEGACY_ORG_IDS = [
        'E15F8010-B7EB-4526-A985-F00D517EBF4C', // Holiday Inn
        'AC9D7FD5-E333-4B1E-9043-4697568313B7', // Radisson Blu
        '62BDEE7F-9CE9-41BE-A80C-EF69F045D56C', // Angelo Plzeň
        '82BB6005-2C25-4EB9-A6CC-DB8BFB4A3C70', // Zimní stadion Nymburk
        '7A67452A-F5D4-430D-B283-8A93B28B425C', // Fairmont Golden Prague
    ].map(s => s.toUpperCase());

    const getDisplayBaseUrl = (useAdminPath) => {
        if (typeof window === 'undefined') return useAdminPath ? 'https://dev.csgcontrols.cz/admin/' : 'https://dev.csgcontrols.cz/';
        const origin = window.location.origin.replace(/\/$/, '');
        return useAdminPath ? `${origin}/admin/` : `${origin}/`;
    };

    /** Pro Holiday Inn: vrátí { urlId, urlRoom, urlOrgID } z dat nebo fallback podle popisu. */
    const getHolidayInnUrlParams = (rowData) => {
        const has = (v) => v != null && v !== '';
        if (has(rowData.url_id)) {
            const urlId = String(rowData.url_id);
            const urlRoom = has(rowData.url_room) ? String(rowData.url_room) : null;
            // Holiday Inn URL conventions (per referenční XLSX "Odkaz zařízení"):
            // - id=1, 9: jen ?id=N
            // - id=11&room=X: meeting room, BEZ orgID
            // - id=12, 13: combined totem A-D / E-F, BEZ orgID (kiosk si HI
            //   dohledá z id v MainJS – viz fallback id==12/13 && !orgID)
            // - id=3: recepce totem, S orgID
            // - id=101&room=X: individual virtual room, BEZ orgID
            const urlOrgID =
                (urlId === '3')
                    ? (has(rowData.url_orgID) ? String(rowData.url_orgID) : HOLIDAY_INN_ORG_ID)
                    : null;
            return { urlId, urlRoom, urlOrgID };
        }
        const desc = (rowData.description || '').toLowerCase().trim();
        const id = rowData.id;
        if (desc.includes('restaurace') && (desc.includes('menu') || !desc.includes('specialit'))) return { urlId: '1', urlRoom: null, urlOrgID: null };
        if (desc.includes('restaurace') && desc.includes('specialit')) return { urlId: '9', urlRoom: null, urlOrgID: null };
        if (desc.includes('recepce') || (desc.includes('obrazovka') && desc.includes('výšku'))) return { urlId: '3', urlRoom: null, urlOrgID: HOLIDAY_INN_ORG_ID };
        if (desc.includes('a - d') || desc.includes('a-d')) return { urlId: '12', urlRoom: null, urlOrgID: null };
        if (desc.includes('e - f') || desc.includes('e-f')) return { urlId: '13', urlRoom: null, urlOrgID: null };
        // Meeting rooms: bez orgID v URL (správně: /admin/?id=11&room=23)
        if (desc.includes('meeting room a') || desc === 'meeting room a') return { urlId: '11', urlRoom: '23', urlOrgID: null };
        if (desc.includes('meeting room b')) return { urlId: '11', urlRoom: '24', urlOrgID: null };
        if (desc.includes('meeting room c')) return { urlId: '11', urlRoom: '25', urlOrgID: null };
        if (desc.includes('meeting room d')) return { urlId: '11', urlRoom: '26', urlOrgID: null };
        if (id === 23) return { urlId: '11', urlRoom: '23', urlOrgID: null };
        if (id === 24) return { urlId: '11', urlRoom: '24', urlOrgID: null };
        if (id === 25) return { urlId: '11', urlRoom: '25', urlOrgID: null };
        if (id === 26) return { urlId: '11', urlRoom: '26', urlOrgID: null };
        return { urlId: Number(rowData.device_group) === 1 ? '101' : '100', urlRoom: String(rowData.id), urlOrgID: null };
    };

    /** Spočítá URL pro libovolná data. Formát URL se určuje podle organizace zařízení (rowData.cOrganization_id). */
    const getDeviceUrlForData = (rowData, orgIdVal, isViennaHouseVal, isDiplomatVal) => {
        const orgId = (rowData.cOrganization_id || orgIdVal || '').toString().trim();
        const orgIdUpper = orgId.toUpperCase();
        const isHolidayInn = orgIdUpper === HOLIDAY_INN_ORG_ID.toUpperCase();
        const isRadissonBlu = orgIdUpper === RADISSON_BLU_ORG_ID.toUpperCase();
        const isDiplomat = Boolean(isDiplomatVal);

        if (isHolidayInn) {
            // /admin/ prefix konzistentní s ostatními org-ami i s reference XLSX
            // (Kiosky - web aplikace ...) — admin "Copy URL" musí matchovat
            // přesně Odkaz zařízení v dokumentaci.
            const base = getDisplayBaseUrl(true);
            const { urlId, urlRoom, urlOrgID } = getHolidayInnUrlParams(rowData);
            const params = new URLSearchParams();
            params.set('id', urlId);
            if (urlRoom) params.set('room', urlRoom);
            if (urlOrgID) params.set('orgID', urlOrgID);
            return `${base}?${params.toString()}`;
        }
        if (isRadissonBlu) {
            const base = getDisplayBaseUrl(true);
            const id = rowData.url_id != null && rowData.url_id !== '' ? String(rowData.url_id) : (Number(rowData.device_group) === 1 ? '5' : '7');
            const room = rowData.url_room != null && rowData.url_room !== '' ? String(rowData.url_room) : String(rowData.id);
            if (Number(rowData.device_group) === 2) return `${base}?id=${id}`;
            return `${base}?id=${id}&room=${room}`;
        }

        // Diplomat Prague: používat stejný /admin runtime formát jako admin prostředí
        // (id=100/101 + room + orgID), aby se URL po přihlášení nepřepisovala na legacy ?id=16.
        if (isDiplomat) {
            const base = getDisplayBaseUrl(true);
            const params = new URLSearchParams();
            const id = rowData.url_id != null && rowData.url_id !== '' ? String(rowData.url_id) : (Number(rowData.device_group) === 1 ? '101' : '100');
            const room = rowData.url_room != null && rowData.url_room !== '' ? String(rowData.url_room) : String(rowData.id);
            params.set('id', id);
            params.set('room', room);
            if (rowData.url_orgID != null && rowData.url_orgID !== '') params.set('orgID', String(rowData.url_orgID));
            else if (orgId) params.set('orgID', orgId);
            if (rowData.url_kiosk != null && rowData.url_kiosk !== '') params.set('kiosk', String(rowData.url_kiosk));
            return `${base}?${params.toString()}`;
        }
        const base = getDisplayBaseUrl(true);
        const params = new URLSearchParams();
        const isLegacy = LEGACY_ORG_IDS.includes(orgIdUpper);
        // Pro non-legacy orgy: id = orgShortId (per-org identifikátor — všechna zařízení
        // v jedné org sdílí stejné id, liší se jen v room).
        // Pro legacy nebo když shortId není: fallback na typ-zobrazení (100/101).
        const id = rowData.url_id != null && rowData.url_id !== ''
            ? String(rowData.url_id)
            : (!isLegacy && rowData.orgShortId != null && rowData.orgShortId !== ''
                ? String(rowData.orgShortId)
                : (Number(rowData.device_group) === 1 ? '101' : '100'));
        const localRoom = (!isLegacy && rowData.localId != null && rowData.localId !== '')
            ? String(rowData.localId)
            : null;
        // Priorita pro room:
        //   - LEGACY orgy: url_room (z DB, dle reference XLSX) → device.id
        //   - NON-LEGACY orgy: localId (per-org sekvence 1, 2, 3...) → url_room → device.id
        //     (localId má přednost, aby zařízení v jedné org dostala 1, 2, 3 napříč
        //      a v URL byl jen krátký room.)
        let room;
        let roomIsLocalId;
        if (!isLegacy && localRoom != null) {
            room = localRoom;
            roomIsLocalId = true;
        } else if (rowData.url_room != null && rowData.url_room !== '') {
            room = String(rowData.url_room);
            roomIsLocalId = localRoom != null && room === localRoom;
        } else if (localRoom != null) {
            room = localRoom;
            roomIsLocalId = true;
        } else {
            room = String(rowData.id);
            roomIsLocalId = false;
        }
        if (isLegacy) {
            params.set('room', room);
            if (rowData.url_orgID != null && rowData.url_orgID !== '') params.set('orgID', String(rowData.url_orgID));
            else if (orgId) params.set('orgID', orgId);
            params.set('id', id);
        } else {
            // Nové orgy: id = orgShortId (per-org), room = localId. orgID v URL NIKDY —
            // backend dohledá org podle shortId přes /resolveOrgDevice.
            params.set('id', id);
            params.set('room', room);
        }
        if (rowData.url_kiosk != null && rowData.url_kiosk !== '') params.set('kiosk', String(rowData.url_kiosk));
        if (isViennaHouseVal && Number(rowData.device_group) === 2) {
            // Vienna House (Plzeň) — kiosk parametr odvodit z konkrétního zařízení:
            //   id=40 "Kiosk - hall"      → kiosk=1 (dolní)
            //   id=41 "Kiosk - 1st floor" → kiosk=2 (horní)
            // Pokud DB má explicitní url_kiosk, použít ho.
            const desc = String(rowData.description || '').toLowerCase();
            let kiosk = '2';
            if (rowData.url_kiosk != null && String(rowData.url_kiosk).trim() !== '') {
                kiosk = String(rowData.url_kiosk).trim();
            } else if (Number(rowData.id) === 40 || desc.includes('hall') || desc.includes('dolní')) {
                kiosk = '1';
            } else if (Number(rowData.id) === 41 || desc.includes('1st floor') || desc.includes('horní')) {
                kiosk = '2';
            }
            return `${base}?id=6&kiosk=${kiosk}`;
        }
        return `${base}?${params.toString()}`;
    };

    const getDeviceUrl = () => getDeviceUrlForData(data, props.orgId, props.isViennaHouse, props.isDiplomat);

    const orgIdUpper = (props.orgId || data.cOrganization_id || '').toString().trim().toUpperCase();
    const isHolidayInn = orgIdUpper === HOLIDAY_INN_ORG_ID.toUpperCase();
    const { urlId } = getHolidayInnUrlParams(data);
    const isRestaurantSpeciality = isHolidayInn && urlId === '9';

    useEffect(() => {
        if (!isRestaurantSpeciality) return;
        let cancelled = false;
        axios.get(`${path}/getCardsV2Holiday`).then((res) => {
            if (!cancelled && Array.isArray(res.data)) setHolidayCards(res.data);
        }).catch(() => { if (!cancelled) setHolidayCards([]); });
        return () => { cancelled = true; };
    }, [isRestaurantSpeciality]);

    const displayName = isRestaurantSpeciality ? 'Restaurace - speciality' : (data.description || `Display ${data.id}`);

    const handleCopyURL = async () => {
        const orgIdVal = props.orgId || data.cOrganization_id;
        const isVienna = props.isViennaHouse || false;
        const isDiplomat = props.isDiplomat || false;
        if (Array.isArray(props.allDisplays) && props.allDisplays.length > 0) {
            console.log('[Row_display] URL pro všechny displeje (orgId=%s, isRadisson=%s):', orgIdVal, (orgIdVal || '').toString().trim().toUpperCase() === RADISSON_BLU_ORG_ID.toUpperCase());
            props.allDisplays.forEach((d) => {
                const url = getDeviceUrlForData(d, orgIdVal, isVienna, isDiplomat);
                console.log(`  ${d.description || d.id} (id=${d.id}, device_group=${d.device_group}): ${url}`);
            });
        } else {
            console.log('[Row_display] URL (tento řádek):', data.description || data.id, getDeviceUrl());
        }
        await copyToClipboard(getDeviceUrl());
    };

    return (
        <div className={classes.row}>
            <div className={classes.column}>
                <div className={classes.nameBlock}>
                    <span>{displayName}</span>
                    {isRestaurantSpeciality && (
                        <span className={classes.contentHint}>
                            {holidayCards.length > 0
                                ? `Obrázky na displeji: ${holidayCards.map((c, i) => (c.description || `Obrázek ${i + 1}`)).join(', ')}`
                                : 'Na displeji zatím žádné obrázky'}
                        </span>
                    )}
                </div>
            </div>
            <div className={classes.column}>
                {data.device_group == 1 ? "Display" : data.device_group == 2 ? "Kiosk" : "Unknown"}
            </div>
            <div className={classes.columnActions}>
                {props.canManageDisplays && !data._synthetic && (
                    <>
                        <Button onClick={handleEdit} color="primary" variant="outlined" size="small">Edit</Button>
                        <Button onClick={handleDelete} color="error" variant="outlined" size="small">Delete</Button>
                    </>
                )}
                {/* Virtuální místnosti bez fyzického displeje — URL tlačítko skrýt.
                    Holiday Inn: E, F, Pop Up, Orangerie, Foyer.
                    Angelo Plzeň (Vienna House org): Opal A-D, Restaurace, Safir, Smaragd, Topaz. */}
                {(() => {
                    const desc = (data.description || '').toLowerCase().trim();
                    const orgUpper = (props.orgId || data.cOrganization_id || '').toString().trim().toUpperCase();
                    const ANG = '62BDEE7F-9CE9-41BE-A80C-EF69F045D56C';
                    const isVirtual = (
                        (isHolidayInn && ['meeting room e','meeting room f','meeting room pop up','orangerie','foyer'].includes(desc)) ||
                        (orgUpper === ANG && ['opal a','opal b','opal c','opal d','restaurace','safir','smaragd','topaz'].includes(desc))
                    );
                    if (isVirtual) return null;
                    return (
                        <Tooltip title={getDeviceUrl()} placement="left">
                            <Button onClick={handleCopyURL} color="secondary" variant="outlined" size="small">URL</Button>
                        </Tooltip>
                    );
                })()}
            </div>
        </div>
    );
};

export default Row_display;
